import type { CaseFileDetail, CaseReading, CaseThread } from "../contract"
import type { Feature } from "../matter/copy"
import type { Stages } from "../../order-sim/engine"
import { outcome as oioOutcome } from "../../oio-sim/decisionModel"
import { outcome as oiaOutcome } from "../../oia-sim/decisionModel"

/** One piece of work done on a case file, named by what it is, opening exactly that work. */
export interface CaseWork {
  key: string
  /** What it is, in plain words: "Notice reading", "Officer's reading", "Appeal or not", "Legal research", "Order draft". */
  what: string
  /** The feature it came from, as the landing doors name it (the chip on the workspace page). */
  feature: Feature
  /** Which order, for an order draft: "OIO" or "OIA". */
  tag?: string
  /** The question asked, for legal research. */
  question?: string
  /** What came of it, in one line. */
  line: string
  /** When it was done (ISO). */
  at: string
  to: string
  /** The words on a button that opens it: "Open the notice reading". */
  open: string
  /** The documents it read, when known: places it on a proceeding. */
  docIds?: string[]
}

/**
 * A research thread holds work only once a question was asked in it. Dropping a notice opens a
 * thread to hold the document; until the reader asks something there it is an empty page, not
 * research, and the case file must not list it or send the reader to it.
 */
export const asked = (t: CaseThread) => t.turns > 0 || !!t.last_question.trim()

/** An order simulator draft started from the case file (the simulator lists it by the case file's id). */
export interface CaseDraft {
  kind: "oio" | "oia"
  id: string
  status: string
  created_at: string
  updated_at: string
  stages: Stages
}

const NOTICE_KINDS = new Set(["notice_no_reply", "notice_with_reply"])
const ORDER_KINDS = new Set(["order_preview", "appeal", "appeal_full"])

function readingLine(r: CaseReading, failed: string): string {
  if (r.status === "running") return "Still being read."
  if (r.status === "failed") return failed
  return r.lead?.line || "Read."
}

/** What came of a draft: the order's outcome in the decisions page's words, else where the run is. */
function draftLine(d: CaseDraft): string {
  const said = (d.kind === "oio" ? oioOutcome : oiaOutcome)(d.stages)
  if (said) return said
  if (d.status === "running") return "Still being drafted."
  if (d.status === "failed") return "The draft stopped. Open it to carry on."
  if (d.status === "awaiting_user") return "Waiting for your answer."
  return "No decision yet."
}

/** Everything done on the case file, newest first. Empty research threads are left out. */
export function caseWork(data: Pick<CaseFileDetail, "id" | "readings" | "threads"> & { drafts?: CaseDraft[] }): CaseWork[] {
  const at = (id: string) => `/case/${encodeURIComponent(data.id)}/reading/${encodeURIComponent(id)}`
  // [work, rank]: on the same moment, the officer's reading sits above the notice reading it was run on.
  const out: [CaseWork, number][] = []
  for (const r of data.readings) {
    if (NOTICE_KINDS.has(r.kind)) {
      out.push([{ key: r.id, what: "Notice reading", feature: "notice", line: readingLine(r, "Nira could not weigh the notice."), at: r.created_at, to: at(r.id), open: "Open the notice reading", docIds: r.inputs }, 1])
      const o = r.officer
      if (o && o.status) {
        const line = o.status === "running" ? "Still being read." : o.status === "failed" ? "Could not be read. Open it to try again." : o.line || "Read."
        out.push([{ key: `${r.id}:officer`, what: "Officer's reading", feature: "notice", line, at: r.created_at, to: `${at(r.id)}#nrd-02`, open: "Open the officer's reading", docIds: r.inputs }, 0])
      }
    } else if (ORDER_KINDS.has(r.kind)) {
      out.push([{ key: r.id, what: "Appeal or not", feature: "appeal", line: readingLine(r, "Nira could not read the order."), at: r.created_at, to: at(r.id), open: "Open Appeal or not", docIds: r.inputs }, 1])
    }
  }
  for (const t of data.threads) {
    if (!asked(t)) continue
    const question = t.last_question.trim() || t.title
    const line = t.turns > 1 ? `${t.turns} questions asked; this was the last.` : "One question asked."
    out.push([{ key: t.id, what: "Legal research", feature: "research", question, line, at: t.updated_at || t.created_at, to: `/nira/${encodeURIComponent(t.id)}`, open: "Open the research" }, 1])
  }
  for (const d of data.drafts ?? []) {
    out.push([{ key: `${d.kind}:${d.id}`, what: "Order draft", feature: d.kind, tag: d.kind.toUpperCase(), line: draftLine(d), at: d.updated_at || d.created_at, to: `/${d.kind}-sim/${encodeURIComponent(d.id)}`, open: "Open the order draft" }, 1])
  }
  return out
    .sort((a, b) => b[0].at.localeCompare(a[0].at) || a[1] - b[1])
    .map(([w]) => w)
}
